import { Body2, Header4, customMediaQueryMax, modalPadding, screenSizeBreakpoints, sectionalMargin, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Button, useTheme } from "@mui/material";
import { Content } from "@iventis/translations";
import { CONTACT_URL, useWindowSize } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { useTreeBrowserInitialise, createInitialState, NodeServices } from "@iventis/tree-browser";
import React, { FunctionComponent, forwardRef, useRef, useState } from "react";
import { Toasts } from "@iventis/toasts/src/toast.types";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { ConfirmationDialogComponent } from "@iventis/components/src/confirmation-dialog";
import { SitemapRootNode, SitemapTreeBrowserContext } from "./sitemap-types";
import { SitemapTreeBrowser } from "./sitemap-tree-browser";

export const SitemapSettings: FunctionComponent<{ services: NodeServices<SitemapRootNode>; toast: Toasts }> = ({ services, toast }) => {
    const [treeBrowserState, treeBrowserContext] = useTreeBrowserInitialise(
        createInitialState<SitemapRootNode>({
            mainNodeId: "root-sitemap-node",
            expandAllNodesOnLoad: true,
        }),
        services
    );

    const translate = useIventisTranslate();
    const addSitemapButtonRef = useRef<HTMLButtonElement>();

    const { screenWidth } = useWindowSize();

    const [createSitemapModalOpen, setCreateSitemapModalOpen] = useState(false);

    return (
        <Container>
            <TextContentContainer>
                <div className="sitemap-settings-row-1">
                    <SitemapSettingsTitle />
                </div>
                <div className="sitemap-settings-row-2">
                    <SitemapSettingsDescription />
                    {screenWidth > screenSizeBreakpoints.small && <AddSitemapButton onClick={() => setCreateSitemapModalOpen(true)} ref={addSitemapButtonRef} />}
                </div>
                {screenWidth <= screenSizeBreakpoints.small && <AddSitemapButton onClick={() => setCreateSitemapModalOpen(true)} ref={addSitemapButtonRef} />}
            </TextContentContainer>
            <IventisErrorBoundaryWithToast toast={toast}>
                {treeBrowserState && (
                    <SitemapTreeBrowserContext.Provider value={treeBrowserContext}>
                        <SitemapTreeBrowser addSitemapButtonRef={addSitemapButtonRef} />
                    </SitemapTreeBrowserContext.Provider>
                )}
            </IventisErrorBoundaryWithToast>
            <ConfirmationDialogComponent
                cancelText={translate(Content.common.buttons.cancel)}
                confirmText={translate(Content.common.buttons.support)}
                handleCancel={() => setCreateSitemapModalOpen(false)}
                show={createSitemapModalOpen}
                title={translate(Content.settings3.sitemaps.add_sitemap)}
                message={translate(Content.settings3.sitemaps.create_sitemap_comming_soon_message)}
                handleConfirm={() => {
                    window.open(CONTACT_URL);
                }}
            />
        </Container>
    );
};

export const AddSitemapButton = forwardRef<HTMLButtonElement, { onClick(): void }>(({ onClick }, ref) => {
    const theme = useTheme<Theme>();
    const translate = useIventisTranslate();

    return (
        <Button ref={ref} variant="outlined" style={{ backgroundColor: theme.typographyColors.blank, minWidth: "fit-content" }} onClick={onClick}>
            {translate(Content.settings3.sitemaps.add_sitemap)}
        </Button>
    );
});

export const SitemapSettingsTitle = () => {
    const translate = useIventisTranslate();
    return <Header4>{translate(Content.settings3.sitemaps.title)}</Header4>;
};

export const SitemapSettingsDescription = () => {
    const translate = useIventisTranslate();
    return <Body2>{translate(Content.settings3.sitemaps.page_content)}</Body2>;
};

const Container = styled.div`
    height: 100%;
    flex: 1 1;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

const TextContentContainer = styled.div`
    margin: ${modalPadding};
    display: flex;
    flex-direction: column;
    row-gap: ${sectionalMargin};

    .sitemap-settings-row-1 {
        width: 100%;
    }
    .sitemap-settings-row-2 {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    ${customMediaQueryMax(screenSizeBreakpoints.small)} {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }
`;
